import React, {
  useEffect,
  useContext,
  createContext,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

export const unitContext = createContext();

// Wrap the hook with a provider
export const ProvideUnit = ({ children }) => {
  const inc = useProvideUnit();
  // We are providing the object returned by useProvideUnit as the value of the unitContext
  return <unitContext.Provider value={inc}>{children}</unitContext.Provider>;
};

ProvideUnit.propTypes = {
  children: PropTypes.element,
};

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useUnit = () => {
  // The context object will contain anything returned by useProvideUnit
  const context = useContext(unitContext);
  if (context === undefined) {
    throw new Error('useUnit must be used within a ProvideUnit');
  }
  return context;
};

const initialState = {
  unit: null,
  id: null,
  version: null,
  stale: true,
  loading: false,
  modifying: false,
  error: null,
};

export const ACTIONS = Object.freeze({
  FETCH_INIT: 'FETCH_INIT',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_FAILURE: 'FETCH_FAILURE',
  SET_MODIFYING: 'SET_MODIFYING',
  SET_STALE: 'SET_STALE',
  SET_VERSION: 'SET_VERSION',
  SET_ID: 'SET_ID',
  DELETE: 'DELETE',
  RESET: 'RESET',
});


const unitReducer = (nextState, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return initialState;
    case ACTIONS.FETCH_INIT:
      return {
        ...nextState,
        loading: true,
        error: null,
      };
    case ACTIONS.FETCH_SUCCESS:
      if (action.payload.deleted) {
        return {
          ...nextState,
          loading: false,
          error: 'Unit has been deleted',
          stale: false,
        };
      }

      return {
        ...nextState,
        loading: false,
        unit: action.payload,
        stale: false,
      };
    case ACTIONS.FETCH_ERROR:
      return {
        ...nextState,
        loading: false,
        unit: null,
        stale: false,
        error: 'Unit not found',
      };
    case ACTIONS.DELETED:
      return {
        ...nextState,
        unit: null,
        stale: true,
      };
    case ACTIONS.FETCH_FAILURE:
      return {
        ...nextState,
        loading: false,
        error: action.payload,
      };
    case ACTIONS.SET_ID: {
      let stale = nextState.id === action.payload ? false : true;
      return {
        ...nextState,
        stale,
        id: action.payload,
      };
    }
    case ACTIONS.SET_VERSION:
      return {
        ...nextState,
        version: action.payload,
      };
    case ACTIONS.SET_MODIFYING:
      return {
        ...nextState,
        modifying: action.payload,
      };
    case ACTIONS.SET_STALE:
      return {
        ...nextState,
        stale: action.payload,
      };
    default:
      throw new Error(`unitReducer this action is not supported: ${action.type}`);
  }
};

const useProvideUnit = () => {
  const controller = useRef(null);
  //unit state as a reducer
  const [unitState, dispatch] = useReducer(unitReducer, initialState);
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const deleteUnit = async () => {
    if (!unitState.id) {
      enqueueSnackbar('Could not delete unit', { variant: 'error'});
      return;
    }

    dispatch({ type: ACTIONS.SET_MODIFYING, payload: true });
    let variant = 'success';
    let message = 'Unit successfully deleted';
    try {
      const response = await axios.post(`/api/unit/delete`, {id: unitState.id});
      nav('/units');
      return response;
    } catch (error) {
      console.log(error);
    } finally {
      enqueueSnackbar(message, { variant});
      dispatch({ type: ACTIONS.SET_MODIFYING, payload: false });
    }
  };

  useEffect(() => {
    const {id, version, stale} = unitState;
    if (stale && id) {
      dispatch({type: ACTIONS.FETCH_INIT})
      let mounted = true;
      controller.current = new AbortController();
      const getUnit = () => {
        axios.get(`/api/unit/get?id=${id}${version === null ? '' : `&version=${version}`}`, { signal: controller.current.signal }).then(res => {
          if (mounted) {
            dispatch({type: ACTIONS.FETCH_SUCCESS, payload: res.data});
          }
        }
        ).catch(error => {
          if (error.name !== 'CanceledError') {
            dispatch({type: ACTIONS.FETCH_ERROR, payload: error});
          }
        });
      }

      getUnit();

      // Cleanup on unmount
      return () => {
        controller.current.abort();
        mounted = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitState.stale, unitState.id]);

  return {
    unmount: () => {
      dispatch({type: ACTIONS.RESET});
      if (unitState.loading) {
        controller.current.abort();
      }
    },
    dispatch,
    deleteUnit,
    unitState,
  }
};
